import React, { useState } from 'react'
import { user, formatters } from '@adc/blueprint'
import { Icon, constants } from '@adc/ui-core'
import { Loader as SemanticLoader } from 'semantic-ui-react'
import type {
  BuyingCriteriaState,
  BuyingPreferenceLocationMatch,
} from '@adc/blueprint'
import classnames from 'classnames/bind'
import styles from './styles.scss'

const cx = classnames.bind(styles)

export interface MatchedPropertiesCardProps {
  preferences: BuyingCriteriaState | null
  variant: 'inline' | 'sidebar'
  hrefBuilder?: (locationCount: BuyingPreferenceLocationMatch) => string
  elmId?: string
}

const MatchedPropertiesCard: React.FC<MatchedPropertiesCardProps> = ({
  preferences,
  variant,
  hrefBuilder,
  elmId = 'matched-properties',
}) => {
  const [expanded, setExpanded] = useState(false)
  const counts = user.hooks.useBuyingPreferenceLocationCounts(preferences)

  const { total, locationCounts, isUnfiltered, isLoading } = counts.data

  return (
    <div className={cx('matched-card', variant)} data-elm-id={elmId}>
      {isLoading ? (
        <div className={cx('matched-header')}>
          <SemanticLoader
            active
            inline='centered'
            size='small'
            data-elm-id={`${elmId}-loading`}
          />
        </div>
      ) : (
        <>
          <div className={cx('matched-header')}>
            <span
              className={cx('matched-number')}
              data-elm-id={`${elmId}-total`}
            >
              {formatters.formatNumber(total)}
            </span>
            <span className={cx('matched-label')}>
              {isUnfiltered ? 'available properties' : 'matching properties'}
            </span>
            {variant === 'sidebar' && locationCounts.length > 0 && (
              <button
                className={cx('expand-toggle', { expanded })}
                onClick={() => setExpanded(!expanded)}
                aria-label={
                  expanded ? 'Collapse locations' : 'Expand locations'
                }
              >
                <Icon
                  name='chevron-down'
                  iconStyle='solid'
                  color={constants.ColorNames.GrayDark}
                />
              </button>
            )}
          </div>

          {variant === 'sidebar' && locationCounts.length > 0 && (
            <ul className={cx('location-list', { collapsed: !expanded })}>
              {locationCounts.map(lc => {
                const hasMatches = lc.active_listing_count > 0
                const where = lc.label ?? ''
                const label = hasMatches
                  ? `View ${formatters.formatNumber(
                      lc.active_listing_count
                    )} in ${where}`
                  : `No matches in ${where}`
                return (
                  <li key={lc.urn ?? `${lc.location_type}-${where}`}>
                    {hrefBuilder ? (
                      <a
                        className={cx('location-link', {
                          'location-link--no-matches': !hasMatches,
                        })}
                        href={hasMatches ? hrefBuilder(lc) : undefined}
                        target='_blank'
                        rel='noopener noreferrer'
                        data-elm-id={`${elmId}-link-${lc.urn ?? where}`}
                      >
                        {label}
                      </a>
                    ) : (
                      <span className={cx('location-text')}>{label}</span>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </>
      )}
    </div>
  )
}

export default MatchedPropertiesCard
